import { useMemo } from 'react';
import useAppStore from '../../../store/rootStore.ts';
import { calculateLiquidationPrice, computeLiquidationBuffer } from '@/utils/liquidationUtils';
import type { ViewPosition } from '@/feature/terminal_tables/position/types';
import type { TokenDetail, TokenNumberFormatOptions } from '@/utils/tokenNumberUtils';

interface UseMarginProjectionInput {
  selectedPosition: ViewPosition | null;
  marginAmountInput: string;
  token: TokenDetail | undefined;
  formatToken: (value: bigint, options?: Partial<TokenNumberFormatOptions>) => string;
  displayOptions: Partial<TokenNumberFormatOptions>;
}

/** Read-only display state for the adjust-margin dialog (parsed input, projections, formatted strings, buffer). */
export function useMarginProjection({
  selectedPosition,
  marginAmountInput,
  token,
  formatToken,
  displayOptions,
}: UseMarginProjectionInput) {
  // Subscribe only to `maintenanceMarginFraction` (effectively static) — not the whole markets map.
  const maintenanceMarginFraction = useAppStore((state) =>
    selectedPosition ? state.markets.get(selectedPosition.marketId)?.maintenanceMarginFraction : undefined
  );

  const formattedCurrentMargin = useMemo(
    () =>
      selectedPosition && token
        ? formatToken(BigInt(Math.round(selectedPosition.margin * 10 ** token.decimals)), displayOptions)
        : '0',
    [selectedPosition, token, formatToken, displayOptions]
  );

  const marginInputAmount = useMemo(() => {
    if (!token) return 0;
    const n = Number.parseFloat(marginAmountInput);
    return Number.isFinite(n) ? n : 0;
  }, [marginAmountInput, token]);

  const projectedValues = useMemo(() => {
    if (!selectedPosition || marginInputAmount <= 0 || maintenanceMarginFraction == null) return null;
    const newMargin = selectedPosition.margin + marginInputAmount;
    // Derive projected leverage from the displayed `leverage` so before/after stays consistent regardless of upstream formula.
    const newLeverage = selectedPosition.margin > 0 ? selectedPosition.leverage * (selectedPosition.margin / newMargin) : 0;
    const newLiqPrice = calculateLiquidationPrice({
      entryPrice: selectedPosition.entryPrice,
      collateral: newMargin,
      size: selectedPosition.size,
      side: selectedPosition.side,
      maintenanceMarginFraction,
      funding: selectedPosition.funding,
    });
    const newLiqBuffer = computeLiquidationBuffer({
      side: selectedPosition.side,
      markPrice: selectedPosition.markPrice,
      liquidationPrice: newLiqPrice,
    });
    return { newMargin, newLeverage, newLiqPrice, newLiqBuffer };
  }, [selectedPosition, marginInputAmount, maintenanceMarginFraction]);

  const formattedProjectedMargin = useMemo(
    () =>
      projectedValues && token
        ? formatToken(BigInt(Math.round(projectedValues.newMargin * 10 ** token.decimals)), displayOptions)
        : '',
    [projectedValues, token, formatToken, displayOptions]
  );

  const currentBuffer = useMemo(
    () =>
      selectedPosition && selectedPosition.liquidationPrice > 0
        ? computeLiquidationBuffer({
            side: selectedPosition.side,
            markPrice: selectedPosition.markPrice,
            liquidationPrice: selectedPosition.liquidationPrice,
          })
        : null,
    [selectedPosition]
  );

  // Stable reference for downstream React.memo consumers.
  return useMemo(
    () => ({ marginInputAmount, projectedValues, formattedCurrentMargin, formattedProjectedMargin, currentBuffer }),
    [marginInputAmount, projectedValues, formattedCurrentMargin, formattedProjectedMargin, currentBuffer]
  );
}

export type MarginProjection = ReturnType<typeof useMarginProjection>;
